import { useState, useEffect, FC } from 'react';
import { deleteRequest, getRequest, postRequest } from '../utils/api';
import { System } from '../interfaces/system';
import { User } from '../interfaces/user';
import { Subscription } from '../interfaces/subscription';
import DropdownModal from '../components/DropdownModal';

const Subscriptions: FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [systems, setSystems] = useState<System[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [newSystemId, setNewSystemId] = useState<number | ''>('');
  const [newUserId, setNewUserId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
    fetchSystems();
    fetchUsers();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const data = await getRequest<Subscription[]>('/subscriptions');
      setSubscriptions(data);
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error fetching subscriptions.');
    }
  };

  const fetchSystems = async () => {
    try {
      const data = await getRequest<System[]>('/systems');
      setSystems(data);
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error fetching systems.');
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getRequest<User[]>('/users');
      setUsers(data);
      setErrorMessage(null);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error fetching users.');
    }
  };

  const handleAddSubscription = async () => {
    if (!newSystemId || !newUserId) {
      setErrorMessage('Please select a system and an user.');
      return;
    }

    try {
      setLoading(true);
      await postRequest('/subscriptions', {
        system: newSystemId,
        user: newUserId,
      });
      setNewSystemId('');
      setNewUserId('');
      setErrorMessage(null);
      fetchSubscriptions();
    } catch (error: any) {
      setErrorMessage(error.message || 'Error adding subscription.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubscription = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteRequest(`/subscriptions/${id}`);
      setErrorMessage(null);
      fetchSubscriptions();
    } catch (error: any) {
      setErrorMessage(error.message || 'Error deleting subscription.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="mb-3">Subscriptions</h2>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      {!isModalOpen ? (
        <button
          className="btn btn-primary"
          onClick={() => setIsModalOpen(true)}
        >
          Add Subscription
        </button>
      ) : (
        <DropdownModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Add Subscription"
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAddSubscription();
            }}
          >
            <div className="mb-3">
              <label htmlFor="system" className="form-label">
                System
              </label>
              <select
                id="system"
                className="form-control"
                value={newSystemId}
                onChange={(e) => setNewSystemId(Number(e.target.value))}
                required
              >
                <option value="">Select System</option>
                {systems.map((system) => (
                  <option key={system.id} value={system.id}>
                    {system.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="user" className="form-label">
                User
              </label>
              <select
                id="user"
                className="form-control"
                value={newUserId}
                onChange={(e) => setNewUserId(Number(e.target.value))}
                required
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              Add Subscription
            </button>
          </form>
        </DropdownModal>
      )}

      <h3 className="mt-4 mb-3">Existing Subscriptions</h3>
      <table className="table table-striped table-bordered">
        <thead className="table-dark">
          <tr>
            <th>ID</th>
            <th>System</th>
            <th>User</th>
            <th>Silenced</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {subscriptions.map((subscription) => (
            <tr key={subscription.id}>
              <td>{subscription.id}</td>
              <td>{subscription.system?.name}</td>
              <td>{subscription.user?.name}</td>
              <td>{subscription.silenced ? 'Yes' : 'No'}</td>
              <td>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteSubscription(subscription.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Subscriptions;
